import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus,
  Edit2,
  Trash2,
  Table,
  Trophy,
  Save,
  Check,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TEAM = {
  team_name: '',
  played: 0,
  wins: 0,
  draws: 0,
  losses: 0,
  goals_for: 0,
  goals_against: 0,
  is_own_team: false
};

export default function LeagueTable() {
  const { user, fetchUser, isAdmin } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_TEAM);
  const [saving, setSaving] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');

  useEffect(() => {
    if (user?.current_season_id) {
      fetchLeagueTable();
    } else {
      setLoading(false);
    }
  }, [user?.current_season_id]);

  useEffect(() => {
    setTitleValue(user?.standings_title || 'Standings');
  }, [user?.standings_title]);

  const fetchLeagueTable = async () => {
    try {
      const response = await axios.get(`${API}/league-table`);
      setTeams(response.data.teams || []);
    } catch (error) {
      console.error('Failed to fetch league table:', error);
      toast.error('Failed to load league table');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTitle = async () => {
    try {
      await axios.put(`${API}/settings`, { standings_title: titleValue });
      await fetchUser();
      setEditingTitle(false);
      toast.success('Title updated');
    } catch (error) {
      toast.error('Failed to update title');
    }
  };

  const handleCancelTitleEdit = () => {
    setTitleValue(user?.standings_title || 'Standings');
    setEditingTitle(false);
  };

  const resetForm = () => {
    setFormData(DEFAULT_TEAM);
    setEditingTeam(null);
    setDialogOpen(false);
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setFormData({
      team_name: team.team_name,
      played: team.played,
      wins: team.wins,
      draws: team.draws,
      losses: team.losses,
      goals_for: team.goals_for,
      goals_against: team.goals_against,
      is_own_team: team.is_own_team
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.team_name.trim()) {
      toast.error('Team name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingTeam) {
        await axios.put(`${API}/league-table/teams/${editingTeam.id}`, formData);
        toast.success('Team updated successfully');
      } else {
        await axios.post(`${API}/league-table/teams`, formData);
        toast.success('Team added successfully');
      }
      fetchLeagueTable();
      resetForm();
    } catch (error) {
      toast.error(editingTeam ? 'Failed to update team' : 'Failed to add team');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    
    try {
      await axios.delete(`${API}/league-table/teams/${teamId}`);
      toast.success('Team deleted successfully');
      fetchLeagueTable();
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  const handleToggleOwnTeam = async (teamId) => {
    try {
      await axios.put(`${API}/league-table/teams/${teamId}/toggle-own-team`);
      fetchLeagueTable();
    } catch (error) {
      toast.error('Failed to update own team');
    }
  };

  const handleInputChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    setFormData(prev => ({ ...prev, [field]: field === 'team_name' ? value : numValue }));
  };

  // Auto-calculate played games from W+D+L
  useEffect(() => {
    const calculatedPlayed = formData.wins + formData.draws + formData.losses;
    if (calculatedPlayed !== formData.played) {
      setFormData(prev => ({ ...prev, played: calculatedPlayed }));
    }
  }, [formData.wins, formData.draws, formData.losses]);

  if (!user?.current_season_id) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center page-fade-in">
          <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mb-6">
            <Table className="w-10 h-10 text-gray-400" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 font-title">No Season Selected</h2>
          <p className="text-gray-500 mb-6">Create a season first to manage your league table.</p>
          <Button 
            onClick={() => window.location.href = '/seasons'}
            className="btn-primary rounded-xl"
          >
            Go to Seasons
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 page-slide-in" data-testid="league-table-container">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">
            League Table
          </h1>
          {isAdmin && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  size="icon"
                  onClick={() => resetForm()}
                  className="rounded-xl shrink-0 bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"
                  data-testid="add-team-btn"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold font-title">
                    {editingTeam ? 'Edit Team' : 'Add New Team'}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    {editingTeam ? 'Edit team details' : 'Add a new team to the league table'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="team_name">Team Name *</Label>
                    <Input
                      id="team_name"
                      value={formData.team_name}
                      onChange={(e) => handleInputChange('team_name', e.target.value)}
                      placeholder="Enter team name"
                      className="rounded-xl"
                      data-testid="team-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="wins">W (Wins)</Label>
                      <Input
                        id="wins"
                        type="number"
                        min="0"
                        value={formData.wins}
                        onChange={(e) => handleInputChange('wins', e.target.value)}
                        className="rounded-xl"
                        data-testid="wins-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="draws">D (Draws)</Label>
                      <Input
                        id="draws"
                        type="number"
                        min="0"
                        value={formData.draws}
                        onChange={(e) => handleInputChange('draws', e.target.value)}
                        className="rounded-xl"
                        data-testid="draws-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="losses">L (Losses)</Label>
                      <Input
                        id="losses"
                        type="number"
                        min="0"
                        value={formData.losses}
                        onChange={(e) => handleInputChange('losses', e.target.value)}
                      className="rounded-xl"
                      data-testid="losses-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="goals_for">F (Goals For)</Label>
                    <Input
                      id="goals_for"
                      type="number"
                      min="0"
                      value={formData.goals_for}
                      onChange={(e) => handleInputChange('goals_for', e.target.value)}
                      className="rounded-xl"
                      data-testid="goals-for-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="goals_against">A (Goals Against)</Label>
                    <Input
                      id="goals_against"
                      type="number"
                      min="0"
                      value={formData.goals_against}
                      onChange={(e) => handleInputChange('goals_against', e.target.value)}
                      className="rounded-xl"
                      data-testid="goals-against-input"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
                    <Label htmlFor="is_own_team" className="cursor-pointer">Mark as my team</Label>
                  </div>
                  <Switch
                    id="is_own_team"
                    checked={formData.is_own_team}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_own_team: checked }))}
                    data-testid="own-team-switch"
                  />
                </div>

                <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>P (Played):</span>
                    <span className="font-medium">{formData.played}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GD (Goal Difference):</span>
                    <span className={`font-medium ${formData.goals_for - formData.goals_against > 0 ? 'text-emerald-600' : formData.goals_for - formData.goals_against < 0 ? 'text-red-600' : ''}`}>
                      {formData.goals_for - formData.goals_against > 0 ? '+' : ''}{formData.goals_for - formData.goals_against}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>PTS (Points):</span>
                    <span className="font-bold text-gray-900">{(formData.wins * 3) + formData.draws}</span>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  {editingTeam && (
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this team?')) {
                          handleDelete(editingTeam.id);
                          resetForm();
                        }
                      }}
                      className="rounded-xl text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      data-testid="delete-team-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Delete
                    </Button>
                  )}
                  <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-xl">
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 btn-primary rounded-xl"
                    disabled={saving}
                    data-testid="save-team-btn"
                  >
                    <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {saving ? 'Saving...' : (editingTeam ? 'Update' : 'Add')} Team
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* League Table */}
        <Card data-testid="league-table-card">
          <CardHeader className="relative">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
                {isAdmin && editingTitle ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={titleValue}
                      onChange={(e) => setTitleValue(e.target.value)}
                      className="h-8 w-40 text-lg font-semibold"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveTitle();
                        if (e.key === 'Escape') handleCancelTitleEdit();
                      }}
                      data-testid="standings-title-input"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSaveTitle}
                      className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                    >
                      <Check className="w-4 h-4" strokeWidth={2} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelTitleEdit}
                      className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    >
                      <X className="w-4 h-4" strokeWidth={2} />
                    </Button>
                  </div>
                ) : (
                  <span>{user?.standings_title || 'Standings'}</span>
                )}
              </CardTitle>
              {isAdmin && !editingTitle && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingTitle(true)}
                  className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  data-testid="edit-standings-title-btn"
                >
                  <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading league table...</div>
            ) : teams.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Table className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Teams Added</h3>
                <p className="text-gray-500 mb-4">
                  {isAdmin ? 'Add teams to start tracking your league standings' : 'No teams in the league table yet'}
                </p>
                {isAdmin && (
                  <Button
                    onClick={() => setDialogOpen(true)}
                    className="btn-primary rounded-xl"
                  >
                    <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                    Add First Team
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-2 sm:px-4 font-semibold text-gray-700">Team</th>
                      <th className="text-center py-3 px-1 sm:px-2 font-semibold text-gray-700 w-10 sm:w-12">P</th>
                      <th className="text-center py-3 px-1 sm:px-2 font-semibold text-gray-700 w-10 sm:w-12">W</th>
                      <th className="text-center py-3 px-1 sm:px-2 font-semibold text-gray-700 w-10 sm:w-12">D</th>
                      <th className="text-center py-3 px-1 sm:px-2 font-semibold text-gray-700 w-10 sm:w-12">L</th>
                      <th className="text-center py-3 px-1 sm:px-2 font-semibold text-gray-700 w-10 sm:w-12">F</th>
                      <th className="text-center py-3 px-1 sm:px-2 font-semibold text-gray-700 w-10 sm:w-12">A</th>
                      <th className="hidden xs:table-cell text-center py-3 px-1 sm:px-2 font-semibold text-gray-700 w-10 sm:w-12">GD</th>
                      <th className="text-center py-3 px-1 sm:px-2 font-semibold text-gray-700 w-12 sm:w-14">PTS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.map((team, index) => {
                      const menuColor = user?.menu_color || '#064E3B';
                      return (
                        <tr 
                          key={team.id} 
                          onClick={() => isAdmin && handleEdit(team)}
                          className={`border-b border-gray-100 transition-colors ${isAdmin ? 'cursor-pointer' : ''} ${
                            team.is_own_team 
                              ? 'font-medium' 
                              : isAdmin ? 'hover:bg-gray-50' : ''
                          }`}
                          style={team.is_own_team ? {
                            backgroundColor: `${menuColor}10`,
                          } : {}}
                          onMouseEnter={(e) => {
                            if (team.is_own_team) {
                              e.currentTarget.style.backgroundColor = `${menuColor}20`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (team.is_own_team) {
                              e.currentTarget.style.backgroundColor = `${menuColor}10`;
                            }
                          }}
                          data-testid={`league-row-${team.id}`}
                        >
                          <td className="py-3 px-2 sm:px-4">
                            <span 
                              className="text-sm sm:text-base"
                              style={team.is_own_team ? { color: menuColor } : { color: '#111827' }}
                            >
                              {team.team_name}
                            </span>
                          </td>
                          <td className="text-center py-3 px-1 sm:px-2 text-sm sm:text-base text-gray-700">{team.played}</td>
                          <td className="text-center py-3 px-1 sm:px-2 text-sm sm:text-base text-emerald-600 font-medium">{team.wins}</td>
                          <td className="text-center py-3 px-1 sm:px-2 text-sm sm:text-base text-gray-600">{team.draws}</td>
                          <td className="text-center py-3 px-1 sm:px-2 text-sm sm:text-base text-red-600 font-medium">{team.losses}</td>
                          <td className="text-center py-3 px-1 sm:px-2 text-sm sm:text-base text-gray-700">{team.goals_for}</td>
                          <td className="text-center py-3 px-1 sm:px-2 text-sm sm:text-base text-gray-700">{team.goals_against}</td>
                          <td className={`hidden xs:table-cell text-center py-3 px-1 sm:px-2 text-sm sm:text-base font-medium ${
                            team.goal_difference > 0 ? 'text-emerald-600' : 
                            team.goal_difference < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {team.goal_difference > 0 ? '+' : ''}{team.goal_difference}
                          </td>
                          <td className="text-center py-3 px-1 sm:px-2">
                            <span 
                              className="text-base sm:text-lg font-bold"
                              style={team.is_own_team ? { color: menuColor } : { color: '#111827' }}
                            >
                              {team.points}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
